import { useRouterState } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { LayoutGrid, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { NavItem } from "@/components/shared/navigation/top-nav";

import { cn } from "@/lib/utils";

interface MoreMenuProps {
  items: readonly NavItem[];
}

export function MoreMenu({ items }: MoreMenuProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isMoreActive = items.some((item) =>
    item.to === "/"
      ? pathname === "/"
      : pathname === item.to || pathname.startsWith(`${item.to}/`),
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isOpen]);

  if (items.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={cn(
          "flex h-nav shrink-0 items-center justify-center gap-2 border-b-4 border-transparent text-base font-medium not-italic text-nav-text transition-colors",
          "hover:text-nav-hover-text",
          isMoreActive && "border-nav-active text-nav-active",
        )}
      >
        <LayoutGrid aria-hidden="true" className="h-5 w-5" />
        <span>{t("common:nav.more")}</span>
      </button>

      {isOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/40 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="more-menu-title"
            className="max-h-[85vh] w-full max-w-more overflow-y-auto rounded-lg bg-bg-elevated p-4 text-nav-text shadow-lg md:p-6"
          >
            <div className="mb-4 flex items-center justify-between border-b border-nav-light-border pb-3 md:justify-start md:border-b-0 md:pb-0">
              <h2 id="more-menu-title" className="text-lg font-semibold text-nav-text">
                {t("common:nav.more")}
              </h2>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label={t("common:nav.closeMore")}
                className="flex h-8 w-8 items-center justify-center rounded-full text-nav-text transition-colors hover:bg-nav-item-hover md:hidden"
              >
                <X aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2 rounded-md border border-nav-border p-2 sm:grid-cols-2 md:gap-3 md:p-3 lg:grid-cols-3">
              {items.map((item) => (
                <MoreMenuItem
                  key={item.to}
                  item={item}
                  onSelect={() => setIsOpen(false)}
                />
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

interface MoreMenuItemProps {
  item: NavItem;
  onSelect: () => void;
}

function MoreMenuItem({ item, onSelect }: MoreMenuItemProps) {
  const { t } = useTranslation();

  return (
    <Link
      to={item.to}
      onClick={onSelect}
      className="flex items-center gap-2 rounded-md p-4 text-base font-medium not-italic text-nav-text transition-colors hover:bg-nav-item-hover"
    >
      <item.icon aria-hidden="true" className="h-5 w-5" />
      <span>{t(item.labelKey)}</span>
    </Link>
  );
}
